/**
 * SubscriptionStatus — the second block on the Subscription tab.
 *
 * Reads v3 pricing for the caller's country and displays the user's *current*
 * tier price. The Payment Cycle cards below let users compare Monthly vs
 * Annual (annual = monthly * 10, 2 months free). Beta-free users see a
 * "no charge during your beta" note and the "Switch to Annual" button is
 * hidden while they are on the trial.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, CheckCircle, Crown, Sparkles } from 'lucide-react';
import { formatCurrency } from '../utils/pricingUtils';
import logger from '../utils/logger';
import { SubscriptionTypeCards, BillingInfoSection } from './subscription/SubscriptionParts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentCycleCards = ({ isMonthly, monthlyAmount, currency, symbol, betaFree, tierLabel }) => {
  if (!monthlyAmount) return null;
  const annual = monthlyAmount * 10;
  const save = monthlyAmount * 2;
  return (
    <div className="pt-4 border-t border-purple-100" data-testid="payment-cycle-cards">
      <h4 className="text-sm font-semibold text-slate-700 mb-1">Choose Payment Cycle</h4>
      <p className="text-xs text-slate-500 mb-3">Prices shown for <strong>{tierLabel}</strong>.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button onClick={() => {}} className={`text-left p-4 rounded-lg border-2 transition-all ${isMonthly ? 'border-emerald-500 bg-emerald-50 shadow-lg' : 'border-slate-200 bg-white hover:border-emerald-300 hover:shadow-md'}`} data-testid="payment-cycle-monthly">
          <div className="flex items-center justify-between mb-2"><div className="flex items-center gap-2"><Calendar className="w-5 h-5 text-emerald-600" /><h5 className="font-semibold text-slate-900">Monthly Pay-As-You-Go</h5></div>{isMonthly && <CheckCircle className="w-5 h-5 text-emerald-600" />}</div>
          <div className="text-2xl font-bold text-slate-900 mb-1">{formatCurrency(monthlyAmount, currency, symbol)}<span className="text-sm font-normal text-slate-500">/month</span></div>
          <ul className="text-xs text-slate-600 space-y-1 mt-2"><li>Billed monthly</li><li>Flexible &mdash; cancel anytime</li><li>Same price no matter how many investments you track</li></ul>
        </button>
        <button
          onClick={() => { if (isMonthly && !betaFree) window.dispatchEvent(new CustomEvent('openSubscriptionModal', { detail: { defaultType: 'annual' } })); }}
          disabled={betaFree}
          className={`text-left p-4 rounded-lg border-2 transition-all relative ${!isMonthly ? 'border-purple-500 bg-purple-50 shadow-lg' : 'border-slate-200 bg-white hover:border-purple-300 hover:shadow-md'} ${betaFree ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}
          data-testid="payment-cycle-annual"
        >
          {isMonthly && !betaFree && <div className="absolute -top-2 -right-2 bg-linear-to-r from-purple-600 to-purple-700 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">Best Value</div>}
          <div className="flex items-center justify-between mb-2"><div className="flex items-center gap-2"><Calendar className="w-5 h-5 text-purple-600" /><h5 className="font-semibold text-slate-900">Annual Payment</h5></div>{!isMonthly && <CheckCircle className="w-5 h-5 text-purple-600" />}</div>
          <div className="text-2xl font-bold text-slate-900 mb-1">{formatCurrency(annual, currency, symbol)}<span className="text-sm font-normal text-slate-500">/year</span></div>
          <div className="text-xs text-emerald-600 font-semibold mb-2">Save {formatCurrency(save, currency, symbol)}/year (2 months FREE!)</div>
          <ul className="text-xs text-slate-600 space-y-1"><li>Billed once per year</li><li>Price locked for 12 months</li><li>Formula: monthly &times; 10 (2 months free)</li></ul>
        </button>
      </div>
      {betaFree ? (
        <p className="text-xs text-slate-500 text-center mt-3" data-testid="payment-cycle-beta-note">
          <Sparkles className="inline w-3 h-3 mr-1 text-emerald-600" />
          You&rsquo;re on your beta trial &mdash; no charge yet. You&rsquo;ll be able to switch cycles once billing begins.
        </p>
      ) : (
        <p className="text-xs text-slate-500 text-center mt-3">{isMonthly ? 'Click Annual Payment above to save 17% on your subscription' : "You're currently on the best value plan."}</p>
      )}
    </div>
  );
};

const SubscriptionStatus = ({ user }) => {
  const [v3, setV3] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const token = localStorage.getItem('auth_token');
      const r = await fetch(`${BACKEND_URL}/api/pricing/v3`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (r.ok) setV3(await r.json());
    } catch (error) { logger.error('Error loading v3 pricing:', error); }
    finally { setIsLoading(false); }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  if (isLoading) return <Card><CardContent className="p-6"><div className="text-center text-slate-500">Loading subscription details...</div></CardContent></Card>;

  const isAnnual = user?.subscription_type === 'annual';
  const isMonthly = !isAnnual;
  const endDate = user?.subscription_end_date ? new Date(user.subscription_end_date) : null;
  const isActive = !endDate || endDate > new Date();
  let daysRemaining = null;
  if (endDate && isActive) daysRemaining = Math.ceil((endDate - new Date()) / (1000 * 60 * 60 * 24));

  const tierKey = v3?.current_tier === 'premium_continuity' ? 'continuity' : 'portfolio';
  const tierLabel = tierKey === 'continuity' ? 'Continuity System' : 'Portfolio Clarity';
  const monthlyAmount = v3?.[tierKey]?.monthly_amount ?? null;
  const currency = v3?.currency || 'USD';
  const symbol = v3?.symbol || '$';
  const betaFree = !!v3?.beta_expires_at;

  return (
    <Card className="border-2 border-purple-200 bg-linear-to-br from-purple-50 to-white" data-testid="subscription-status-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-slate-900"><Crown className="w-5 h-5 text-purple-600" />Subscription Status</CardTitle>
            <CardDescription className="mt-1">Your <strong>{tierLabel}</strong> plan</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {user?.rate_label && (
              <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-800 border-emerald-300" data-testid="subscription-rate-badge" title={`${user.discount_percent}% off the standard rate`}>{user.rate_label} &middot; {user.discount_percent}% off</Badge>
            )}
            <Badge variant={isActive ? 'success' : 'secondary'} className="text-sm px-3 py-1">{isActive ? 'Active' : 'Inactive'}</Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <SubscriptionTypeCards isAnnual={isAnnual} currentMonthlyCost={monthlyAmount} currency={currency} symbol={symbol} pricingInfo={null} />
        <BillingInfoSection isAnnual={isAnnual} endDate={endDate} daysRemaining={daysRemaining} currentMonthlyCost={monthlyAmount} currency={currency} symbol={symbol} />
        <PaymentCycleCards isMonthly={isMonthly} monthlyAmount={monthlyAmount} currency={currency} symbol={symbol} betaFree={betaFree} tierLabel={tierLabel} />
        <div className="pt-4 border-t border-purple-100"><p className="text-xs text-slate-500 text-center">Need help with billing? Contact <a className="underline" href="mailto:support@myinvestfolio.io">support@myinvestfolio.io</a></p></div>
      </CardContent>
    </Card>
  );
};

export default SubscriptionStatus;
